import { ref, watch } from 'vue';

export const LOADING_PHASES = {
  AUTHENTICATING: 'authenticating',
  CONNECTING: 'connecting',
  STARTING: 'starting',
  PREPARING: 'preparing',
};

const PREPARING_DELAY_MS = 4000;

function messageHasAiContent(message) {
  const content = message?.content;
  if (typeof content === 'string') return content.length > 0;
  if (Array.isArray(content)) return content.length > 0;
  return false;
}

export function createCopilotLoadingPhaseController() {
  const loadingPhase = ref(null);
  let prepareTimer = null;

  function clearPrepareTimer() {
    if (prepareTimer) {
      clearTimeout(prepareTimer);
      prepareTimer = null;
    }
  }

  function setLoadingPhase(phase) {
    loadingPhase.value = phase;
    clearPrepareTimer();

    if (phase === LOADING_PHASES.STARTING) {
      prepareTimer = setTimeout(() => {
        if (loadingPhase.value === LOADING_PHASES.STARTING) {
          loadingPhase.value = LOADING_PHASES.PREPARING;
        }
      }, PREPARING_DELAY_MS);
    }
  }

  function clearLoadingPhase() {
    clearPrepareTimer();
    loadingPhase.value = null;
  }

  return { loadingPhase, setLoadingPhase, clearLoadingPhase };
}

export function watchCopilotLoadingPhase({
  stream,
  toolCalls,
  loadingPhase,
  clearLoadingPhase,
}) {
  watch(
    () => stream.isLoading.value,
    loading => {
      if (!loading) clearLoadingPhase();
    }
  );

  watch(
    [() => stream.messages.value, toolCalls, () => stream.isLoading.value],
    () => {
      if (!stream.isLoading.value || !loadingPhase.value) return;

      const hasActiveTools = (toolCalls.value || []).some(
        tc => tc.status === 'running' || tc.status === 'pending'
      );
      if (hasActiveTools) {
        clearLoadingPhase();
        return;
      }

      const msgs = stream.messages.value || [];
      const lastAi = [...msgs].reverse().find(msg => {
        // eslint-disable-next-line no-underscore-dangle
        const type = msg?.type || msg?._getType?.();
        return type === 'ai';
      });
      if (messageHasAiContent(lastAi)) clearLoadingPhase();
    },
    { deep: true }
  );
}
